// Sidebar music easter egg — synthesized chiptune (Web Audio oscillators only;
// no audio file bundled), toggled from the sidebar (see use-wii-shop-music.ts).
// One engine drives two tracks: the Wii Shop Channel theme for everyone, and the
// Trololo song for Elad. Both are note-for-note transcriptions from MIDIs.
//
// Notes are transcribed from a MIDI of the original (Kazumi Totaka) — the
// 64-beat main loop: section A (sax hook "A G F# E ...") into section B (the
// high organ melody), over the walking bass. Each lead note is double-struck,
// matching the original's swung comping. The MIDI is authored at half-time, so
// we play it back at ~118 BPM to match the real groove. Swing is baked into the
// note timings, so playback is linear.

type Voice = "bass" | "chord" | "lead";

interface NoteEvent {
  voice: Voice;
  freq: number;
  beat: number; // start, in beats from loop start
  beats: number; // duration in beats
}

interface VoiceParams {
  type: OscillatorType;
  gain: number;
  sustain: number;
  release: number;
}

const MASTER_GAIN = 0.12;
const LOOKAHEAD_MS = 25;
const SCHEDULE_AHEAD_S = 0.12;

const VOICE: Record<Voice, VoiceParams> = {
  bass: { type: "triangle", gain: 0.42, sustain: 0.7, release: 0.07 },
  chord: { type: "triangle", gain: 0.06, sustain: 0.6, release: 0.05 },
  lead: { type: "square", gain: 0.2, sustain: 0.7, release: 0.08 },
};

const SEMITONE_FROM_C: Record<string, number> = {
  C: 0,
  "C#": 1,
  D: 2,
  "D#": 3,
  E: 4,
  F: 5,
  "F#": 6,
  G: 7,
  "G#": 8,
  A: 9,
  "A#": 10,
  B: 11,
};

function noteToFreq(note: string): number {
  const match = /^([A-G]#?)(-?\d)$/.exec(note);
  const name = match?.[1];
  const octave = match?.[2];
  if (name === undefined || octave === undefined) {
    throw new Error(`wii-shop-music: bad note "${note}"`);
  }
  const semitone = SEMITONE_FROM_C[name];
  if (semitone === undefined) {
    throw new Error(`wii-shop-music: bad note "${note}"`);
  }
  const midi = (Number(octave) + 1) * 12 + semitone;
  return 440 * 2 ** ((midi - 69) / 12);
}

// [voice, note, startBeat, durationBeats] — transcribed from the MIDI.
// Wii Shop Channel — 64-beat main loop, 118 BPM.
const WII_SHOP_SONG: ReadonlyArray<readonly [Voice, string, number, number]> = [
  ["bass", "G2", 0.001, 0.755],
  ["bass", "G2", 0.757, 0.212],
  ["bass", "D2", 1.008, 0.745],
  ["bass", "D2", 1.754, 0.213],
  ["bass", "G2", 2.006, 0.746],
  ["bass", "G2", 2.753, 0.211],
  ["bass", "D2", 3.003, 0.746],
  ["bass", "D2", 3.75, 0.211],
  ["bass", "G2", 4.001, 0.755],
  ["bass", "G2", 4.757, 0.212],
  ["bass", "D2", 5.008, 0.745],
  ["bass", "D2", 5.754, 0.213],
  ["bass", "G2", 6.006, 0.746],
  ["bass", "G2", 6.753, 0.211],
  ["bass", "D2", 7.003, 0.746],
  ["bass", "D2", 7.75, 0.211],
  ["bass", "C2", 8.001, 0.755],
  ["bass", "C2", 8.757, 0.212],
  ["bass", "G2", 9.008, 0.745],
  ["bass", "G2", 9.754, 0.213],
  ["bass", "C2", 10.006, 0.746],
  ["bass", "C2", 10.753, 0.211],
  ["bass", "G2", 11.003, 0.746],
  ["bass", "G2", 11.75, 0.211],
  ["bass", "C2", 12.001, 0.755],
  ["bass", "C2", 12.757, 0.212],
  ["bass", "G2", 13.008, 0.745],
  ["bass", "G2", 13.754, 0.213],
  ["bass", "F2", 14.006, 0.746],
  ["bass", "F2", 14.753, 0.211],
  ["bass", "C2", 15.003, 0.459],
  ["bass", "B2", 16.001, 0.755],
  ["bass", "B2", 16.757, 0.212],
  ["bass", "F#2", 17.008, 0.745],
  ["bass", "F#2", 17.754, 0.213],
  ["bass", "A#2", 18.006, 0.746],
  ["bass", "A#2", 18.753, 0.211],
  ["bass", "E2", 19.003, 0.746],
  ["bass", "E2", 19.75, 0.211],
  ["bass", "A2", 20.001, 0.755],
  ["bass", "A2", 20.757, 0.212],
  ["bass", "E2", 21.008, 0.745],
  ["bass", "E2", 21.754, 0.213],
  ["bass", "G#2", 22.006, 0.746],
  ["bass", "G#2", 22.753, 0.211],
  ["bass", "D2", 23.003, 0.746],
  ["bass", "D2", 23.75, 0.211],
  ["bass", "A2", 24.001, 0.755],
  ["bass", "A2", 24.757, 0.212],
  ["bass", "E2", 25.008, 0.708],
  ["bass", "G#2", 25.754, 0.251],
  ["bass", "G#2", 26.006, 0.746],
  ["bass", "G#2", 26.753, 0.211],
  ["bass", "D#2", 27.003, 0.708],
  ["bass", "G2", 27.75, 0.249],
  ["bass", "G2", 28.001, 0.755],
  ["bass", "G2", 28.757, 0.212],
  ["bass", "D2", 29.008, 0.745],
  ["bass", "D2", 29.754, 0.213],
  ["bass", "F#2", 30.006, 0.746],
  ["bass", "F#2", 30.753, 0.211],
  ["bass", "B1", 31.003, 0.746],
  ["bass", "B1", 31.75, 0.211],
  ["bass", "E2", 32.001, 0.755],
  ["bass", "E2", 32.757, 0.212],
  ["bass", "B1", 33.008, 0.745],
  ["bass", "B1", 33.754, 0.213],
  ["bass", "E2", 34.006, 0.746],
  ["bass", "E2", 34.753, 0.211],
  ["bass", "B1", 35.003, 0.746],
  ["bass", "B1", 35.75, 0.211],
  ["bass", "E2", 36.001, 0.755],
  ["bass", "E2", 36.757, 0.212],
  ["bass", "B1", 37.008, 0.745],
  ["bass", "B1", 37.754, 0.213],
  ["bass", "E2", 38.006, 0.746],
  ["bass", "E2", 38.753, 0.211],
  ["bass", "B1", 39.003, 0.746],
  ["bass", "B1", 39.75, 0.211],
  ["bass", "E2", 40.001, 0.755],
  ["bass", "E2", 40.757, 0.212],
  ["bass", "B1", 41.008, 0.745],
  ["bass", "B1", 41.754, 0.213],
  ["bass", "A1", 42.006, 0.746],
  ["bass", "A1", 42.753, 0.211],
  ["bass", "E2", 43.003, 0.746],
  ["bass", "E2", 43.75, 0.211],
  ["bass", "D2", 44.001, 0.755],
  ["bass", "D2", 44.757, 0.212],
  ["bass", "A2", 45.008, 0.745],
  ["bass", "A2", 45.754, 0.213],
  ["bass", "D#2", 46.006, 0.746],
  ["bass", "D#2", 46.753, 0.211],
  ["bass", "D2", 47.003, 0.746],
  ["bass", "D2", 47.75, 0.211],
  ["bass", "C#2", 48.001, 0.755],
  ["bass", "C#2", 48.757, 0.212],
  ["bass", "G#2", 49.008, 0.745],
  ["bass", "G#2", 49.754, 0.213],
  ["bass", "C2", 50.006, 0.746],
  ["bass", "C2", 50.753, 0.211],
  ["bass", "F#2", 51.003, 0.746],
  ["bass", "F#2", 51.75, 0.211],
  ["bass", "B1", 52.001, 0.755],
  ["bass", "B1", 52.757, 0.212],
  ["bass", "F#2", 53.008, 0.745],
  ["bass", "F#2", 53.754, 0.213],
  ["bass", "E2", 54.006, 0.746],
  ["bass", "E2", 54.753, 0.211],
  ["bass", "B1", 55.003, 0.746],
  ["bass", "B1", 55.75, 0.211],
  ["bass", "A1", 56.001, 0.259],
  ["bass", "A2", 56.001, 0.259],
  ["bass", "A1", 56.261, 0.247],
  ["bass", "A2", 56.261, 0.247],
  ["bass", "A1", 56.509, 0.247],
  ["bass", "A2", 56.509, 0.247],
  ["bass", "A1", 56.757, 0.25],
  ["bass", "A2", 56.757, 0.25],
  ["bass", "A1", 57.008, 0.212],
  ["bass", "A2", 57.008, 0.212],
  ["bass", "A1", 58.505, 0.247],
  ["bass", "A2", 58.505, 0.247],
  ["bass", "A1", 58.753, 0.249],
  ["bass", "A2", 58.753, 0.249],
  ["bass", "A1", 59.003, 0.249],
  ["bass", "A2", 59.003, 0.249],
  ["bass", "A1", 59.253, 0.247],
  ["bass", "A2", 59.253, 0.247],
  ["bass", "A1", 59.501, 0.21],
  ["bass", "A2", 59.501, 0.248],
  ["bass", "A1", 60.001, 0.259],
  ["bass", "A2", 60.001, 0.259],
  ["bass", "A1", 60.261, 0.247],
  ["bass", "A2", 60.261, 0.247],
  ["bass", "A1", 60.509, 0.247],
  ["bass", "A2", 60.509, 0.247],
  ["bass", "A1", 60.757, 0.25],
  ["bass", "A2", 60.757, 0.25],
  ["bass", "A1", 61.008, 0.214],
  ["bass", "A2", 61.008, 0.214],
  ["bass", "A1", 63.003, 0.125],
  ["bass", "A2", 63.003, 0.125],
  ["bass", "A1", 63.129, 0.123],
  ["bass", "A2", 63.129, 0.123],
  ["bass", "A1", 63.253, 0.123],
  ["bass", "A2", 63.253, 0.123],
  ["bass", "A1", 63.377, 0.123],
  ["bass", "A2", 63.377, 0.123],
  ["bass", "A1", 63.501, 0.236],
  ["bass", "A2", 63.501, 0.236],
  ["chord", "B3", 0.261, 0.097],
  ["chord", "D4", 0.261, 0.097],
  ["chord", "B3", 0.509, 0.097],
  ["chord", "D4", 0.509, 0.059],
  ["chord", "B3", 1.008, 0.1],
  ["chord", "D4", 1.008, 0.1],
  ["chord", "B3", 1.259, 0.096],
  ["chord", "D4", 1.259, 0.058],
  ["chord", "B3", 1.754, 0.101],
  ["chord", "D4", 1.754, 0.101],
  ["chord", "B3", 2.006, 0.1],
  ["chord", "D4", 2.006, 0.062],
  ["chord", "B3", 2.505, 0.097],
  ["chord", "D4", 2.505, 0.097],
  ["chord", "B3", 2.753, 0.099],
  ["chord", "D4", 2.753, 0.061],
  ["chord", "B3", 4.261, 0.097],
  ["chord", "D4", 4.261, 0.097],
  ["chord", "B3", 4.509, 0.097],
  ["chord", "D4", 4.509, 0.059],
  ["chord", "B3", 5.008, 0.1],
  ["chord", "D4", 5.008, 0.1],
  ["chord", "B3", 5.259, 0.096],
  ["chord", "D4", 5.259, 0.058],
  ["chord", "B3", 5.754, 0.101],
  ["chord", "D4", 5.754, 0.101],
  ["chord", "B3", 6.006, 0.1],
  ["chord", "D4", 6.006, 0.062],
  ["chord", "B3", 6.505, 0.097],
  ["chord", "D4", 6.505, 0.097],
  ["chord", "B3", 6.753, 0.099],
  ["chord", "D4", 6.753, 0.061],
  ["chord", "E4", 8.261, 0.097],
  ["chord", "G4", 8.261, 0.097],
  ["chord", "E4", 8.509, 0.097],
  ["chord", "G4", 8.509, 0.059],
  ["chord", "E4", 9.008, 0.1],
  ["chord", "G4", 9.008, 0.1],
  ["chord", "E4", 9.259, 0.096],
  ["chord", "G4", 9.259, 0.058],
  ["chord", "E4", 9.754, 0.101],
  ["chord", "G4", 9.754, 0.101],
  ["chord", "E4", 10.006, 0.1],
  ["chord", "G4", 10.006, 0.062],
  ["chord", "E4", 10.505, 0.097],
  ["chord", "G4", 10.505, 0.097],
  ["chord", "E4", 10.753, 0.099],
  ["chord", "G4", 10.753, 0.061],
  ["chord", "D#4", 12.261, 0.097],
  ["chord", "G4", 12.261, 0.097],
  ["chord", "D#4", 12.509, 0.097],
  ["chord", "G4", 12.509, 0.059],
  ["chord", "D#4", 13.008, 0.1],
  ["chord", "G4", 13.008, 0.1],
  ["chord", "D#4", 13.259, 0.096],
  ["chord", "G4", 13.259, 0.058],
  ["chord", "D#4", 13.754, 0.101],
  ["chord", "G4", 13.754, 0.101],
  ["chord", "D#4", 14.006, 0.1],
  ["chord", "G4", 14.006, 0.062],
  ["chord", "C4", 14.505, 0.097],
  ["chord", "D#4", 14.505, 0.097],
  ["chord", "C4", 14.753, 0.099],
  ["chord", "D#4", 14.753, 0.061],
  ["chord", "B3", 17.008, 0.197],
  ["chord", "D4", 17.008, 0.197],
  ["chord", "D4", 17.506, 0.097],
  ["chord", "F#4", 17.506, 0.059],
  ["chord", "A#3", 17.754, 2.207],
  ["chord", "C#4", 17.754, 0.251],
  ["chord", "F#3", 20.757, 0.212],
  ["chord", "A3", 20.757, 0.25],
  ["chord", "A3", 21.008, 0.1],
  ["chord", "C4", 21.008, 0.1],
  ["chord", "A3", 21.259, 0.096],
  ["chord", "C4", 21.259, 0.096],
  ["chord", "A3", 21.506, 0.097],
  ["chord", "C4", 21.506, 0.097],
  ["chord", "C4", 21.754, 0.101],
  ["chord", "E4", 21.754, 0.062],
  ["chord", "D4", 22.006, 0.996],
  ["chord", "F4", 22.006, 0.996],
  ["chord", "D4", 23.003, 0.099],
  ["chord", "F4", 23.003, 0.061],
  ["chord", "C4", 24.001, 0.108],
  ["chord", "E4", 24.001, 0.07],
  ["chord", "A3", 24.509, 0.097],
  ["chord", "C4", 24.509, 0.097],
  ["chord", "A3", 24.757, 0.1],
  ["chord", "C4", 24.757, 0.062],
  ["chord", "C4", 25.259, 0.096],
  ["chord", "E4", 25.259, 0.058],
  ["chord", "C4", 25.754, 0.464],
  ["chord", "D#4", 25.754, 0.464],
  ["chord", "G#3", 26.505, 0.097],
  ["chord", "C4", 26.505, 0.097],
  ["chord", "G#3", 26.753, 0.099],
  ["chord", "C4", 26.753, 0.061],
  ["chord", "C4", 27.253, 0.097],
  ["chord", "D#4", 27.253, 0.059],
  ["chord", "B3", 27.75, 1.219],
  ["chord", "D4", 27.75, 1.219],
  ["chord", "F#5", 28.509, 0.097],
  ["chord", "B5", 28.509, 0.059],
  ["chord", "F#5", 29.259, 0.096],
  ["chord", "B5", 29.259, 0.058],
  ["chord", "A5", 30.505, 0.097],
  ["chord", "C#6", 30.505, 0.097],
  ["chord", "A5", 30.753, 0.099],
  ["chord", "C#6", 30.753, 0.061],
  ["chord", "B5", 31.003, 0.099],
  ["chord", "B5", 31.253, 0.097],
  ["chord", "B5", 31.501, 0.098],
  ["chord", "G#5", 32.261, 0.097],
  ["chord", "B5", 32.261, 0.097],
  ["chord", "G#5", 32.509, 0.097],
  ["chord", "B5", 32.509, 0.059],
  ["chord", "G#5", 33.008, 0.1],
  ["chord", "B5", 33.008, 0.1],
  ["chord", "G#5", 33.259, 0.096],
  ["chord", "B5", 33.259, 0.058],
  ["chord", "G#5", 33.754, 0.101],
  ["chord", "B5", 33.754, 0.101],
  ["chord", "G#5", 34.006, 0.1],
  ["chord", "B5", 34.006, 0.062],
  ["chord", "G#5", 34.505, 0.097],
  ["chord", "B5", 34.505, 0.097],
  ["chord", "G#5", 34.753, 0.099],
  ["chord", "B5", 34.753, 0.061],
  ["chord", "G#5", 36.261, 0.097],
  ["chord", "B5", 36.261, 0.097],
  ["chord", "G#5", 36.509, 0.097],
  ["chord", "B5", 36.509, 0.059],
  ["chord", "G#5", 37.008, 0.1],
  ["chord", "B5", 37.008, 0.1],
  ["chord", "G#5", 37.259, 0.096],
  ["chord", "B5", 37.259, 0.058],
  ["chord", "G#5", 37.754, 0.101],
  ["chord", "B5", 37.754, 0.101],
  ["chord", "G#5", 38.006, 0.1],
  ["chord", "B5", 38.006, 0.062],
  ["chord", "G#5", 38.505, 0.097],
  ["chord", "B5", 38.505, 0.097],
  ["chord", "G#5", 38.753, 0.099],
  ["chord", "B5", 38.753, 0.061],
  ["chord", "G5", 40.261, 0.097],
  ["chord", "B5", 40.261, 0.097],
  ["chord", "G5", 40.509, 0.097],
  ["chord", "B5", 40.509, 0.059],
  ["chord", "G5", 41.008, 0.1],
  ["chord", "B5", 41.008, 0.1],
  ["chord", "G5", 41.259, 0.096],
  ["chord", "B5", 41.259, 0.058],
  ["chord", "G5", 41.754, 0.101],
  ["chord", "B5", 41.754, 0.101],
  ["chord", "G5", 42.006, 0.1],
  ["chord", "B5", 42.006, 0.062],
  ["chord", "G5", 42.505, 0.097],
  ["chord", "B5", 42.505, 0.097],
  ["chord", "G5", 42.753, 0.099],
  ["chord", "B5", 42.753, 0.061],
  ["chord", "F#5", 44.261, 0.097],
  ["chord", "A5", 44.261, 0.097],
  ["chord", "F#5", 44.509, 0.097],
  ["chord", "A5", 44.509, 0.059],
  ["chord", "F#5", 45.008, 0.1],
  ["chord", "A5", 45.008, 0.1],
  ["chord", "F#5", 45.259, 0.096],
  ["chord", "A5", 45.259, 0.058],
  ["chord", "F#5", 45.754, 0.101],
  ["chord", "A5", 45.754, 0.101],
  ["chord", "F#5", 46.006, 0.1],
  ["chord", "A5", 46.006, 0.062],
  ["chord", "F#5", 46.505, 0.097],
  ["chord", "A5", 46.505, 0.097],
  ["chord", "F#5", 46.753, 0.099],
  ["chord", "A5", 46.753, 0.061],
  ["chord", "E5", 48.509, 0.097],
  ["chord", "E5", 48.757, 0.1],
  ["chord", "G#5", 49.259, 0.096],
  ["chord", "C5", 49.754, 1.708],
  ["chord", "D#5", 49.754, 1.708],
  ["chord", "B4", 52.261, 0.097],
  ["chord", "D5", 52.261, 0.059],
  ["chord", "B4", 52.757, 0.25],
  ["chord", "D5", 52.757, 0.25],
  ["chord", "B4", 53.008, 0.1],
  ["chord", "D5", 53.008, 0.1],
  ["chord", "B4", 53.259, 0.096],
  ["chord", "D5", 53.259, 0.096],
  ["chord", "B4", 53.506, 0.097],
  ["chord", "D5", 53.506, 0.097],
  ["chord", "D5", 53.754, 0.101],
  ["chord", "F#5", 53.754, 0.062],
  ["chord", "E5", 54.006, 0.996],
  ["chord", "G#5", 54.006, 0.996],
  ["chord", "E5", 55.003, 0.099],
  ["chord", "G#5", 55.003, 0.061],
  ["chord", "C#5", 56.001, 0.108],
  ["chord", "E5", 56.001, 0.108],
  ["chord", "G#5", 56.001, 0.108],
  ["chord", "C#5", 56.261, 0.097],
  ["chord", "E5", 56.261, 0.097],
  ["chord", "G#5", 56.261, 0.097],
  ["chord", "C#5", 56.509, 0.097],
  ["chord", "E5", 56.509, 0.097],
  ["chord", "G#5", 56.509, 0.097],
  ["chord", "C#5", 56.757, 0.1],
  ["chord", "E5", 56.757, 0.1],
  ["chord", "G#5", 56.757, 0.1],
  ["chord", "C#5", 57.008, 0.1],
  ["chord", "E5", 57.008, 0.062],
  ["chord", "G#5", 57.008, 0.062],
  ["chord", "C#5", 58.505, 0.097],
  ["chord", "E5", 58.505, 0.097],
  ["chord", "G#5", 58.505, 0.097],
  ["chord", "C#5", 58.753, 0.099],
  ["chord", "E5", 58.753, 0.099],
  ["chord", "G#5", 58.753, 0.099],
  ["chord", "C#5", 59.003, 0.099],
  ["chord", "E5", 59.003, 0.099],
  ["chord", "G#5", 59.003, 0.099],
  ["chord", "C#5", 59.253, 0.097],
  ["chord", "E5", 59.253, 0.097],
  ["chord", "G#5", 59.253, 0.097],
  ["chord", "C#5", 59.501, 0.098],
  ["chord", "E5", 59.501, 0.06],
  ["chord", "G#5", 59.501, 0.06],
  ["chord", "C5", 60.001, 0.108],
  ["chord", "E5", 60.001, 0.108],
  ["chord", "G5", 60.001, 0.108],
  ["chord", "C5", 60.261, 0.097],
  ["chord", "E5", 60.261, 0.097],
  ["chord", "G5", 60.261, 0.097],
  ["chord", "C5", 60.509, 0.097],
  ["chord", "E5", 60.509, 0.097],
  ["chord", "G5", 60.509, 0.097],
  ["chord", "C5", 60.757, 0.1],
  ["chord", "E5", 60.757, 0.1],
  ["chord", "G5", 60.757, 0.1],
  ["chord", "C5", 61.008, 0.1],
  ["chord", "E5", 61.008, 0.063],
  ["chord", "G5", 61.008, 0.063],
  ["chord", "C5", 63.003, 0.125],
  ["chord", "E5", 63.003, 0.125],
  ["chord", "F#5", 63.003, 0.125],
  ["chord", "C5", 63.129, 0.123],
  ["chord", "E5", 63.129, 0.123],
  ["chord", "F#5", 63.129, 0.123],
  ["chord", "C5", 63.253, 0.123],
  ["chord", "E5", 63.253, 0.123],
  ["chord", "F#5", 63.253, 0.123],
  ["chord", "C5", 63.377, 0.123],
  ["chord", "E5", 63.377, 0.123],
  ["chord", "F#5", 63.377, 0.123],
  ["chord", "C5", 63.501, 0.098],
  ["chord", "E5", 63.501, 0.086],
  ["chord", "F#5", 63.501, 0.086],
  ["lead", "A4", 0.261, 0.097],
  ["lead", "A4", 0.509, 0.097],
  ["lead", "G4", 1.008, 0.1],
  ["lead", "G4", 1.259, 0.096],
  ["lead", "F#4", 1.754, 0.101],
  ["lead", "F#4", 2.006, 0.1],
  ["lead", "E4", 2.505, 0.097],
  ["lead", "E4", 2.753, 0.099],
  ["lead", "A4", 4.261, 0.097],
  ["lead", "A4", 4.509, 0.097],
  ["lead", "G4", 5.008, 0.1],
  ["lead", "G4", 5.259, 0.096],
  ["lead", "F#4", 5.754, 0.101],
  ["lead", "F#4", 6.006, 0.1],
  ["lead", "E4", 6.505, 0.097],
  ["lead", "E4", 6.753, 0.099],
  ["lead", "D5", 8.261, 0.097],
  ["lead", "D5", 8.509, 0.097],
  ["lead", "C5", 9.008, 0.1],
  ["lead", "C5", 9.259, 0.096],
  ["lead", "B4", 9.754, 0.101],
  ["lead", "B4", 10.006, 0.1],
  ["lead", "A4", 10.505, 0.097],
  ["lead", "A4", 10.753, 0.099],
  ["lead", "D5", 12.261, 0.097],
  ["lead", "D5", 12.509, 0.097],
  ["lead", "C5", 13.008, 0.1],
  ["lead", "C5", 13.259, 0.096],
  ["lead", "A#4", 13.754, 0.101],
  ["lead", "A#4", 14.006, 0.1],
  ["lead", "A4", 14.505, 0.097],
  ["lead", "A4", 14.753, 0.099],
  ["lead", "F#4", 17.008, 0.197],
  ["lead", "A4", 17.506, 0.097],
  ["lead", "E4", 17.754, 2.245],
  ["lead", "C#4", 18.006, 1.955],
  ["lead", "D#4", 20.757, 0.25],
  ["lead", "E4", 21.008, 0.1],
  ["lead", "F#4", 21.259, 0.096],
  ["lead", "G4", 21.506, 0.097],
  ["lead", "A4", 21.754, 0.101],
  ["lead", "B4", 22.006, 0.996],
  ["lead", "C5", 23.003, 0.099],
  ["lead", "B4", 24.001, 0.108],
  ["lead", "E4", 24.509, 0.097],
  ["lead", "E4", 24.757, 0.1],
  ["lead", "B4", 25.259, 0.096],
  ["lead", "A#4", 25.754, 0.502],
  ["lead", "D#4", 26.505, 0.097],
  ["lead", "D#4", 26.753, 0.099],
  ["lead", "A#4", 27.253, 0.097],
  ["lead", "A4", 27.75, 1.219],
  ["lead", "D6", 28.509, 0.097],
  ["lead", "D6", 29.259, 0.096],
  ["lead", "E6", 30.505, 0.097],
  ["lead", "E6", 30.753, 0.099],
  ["lead", "D#6", 31.003, 0.099],
  ["lead", "D#6", 31.253, 0.097],
  ["lead", "D#6", 31.501, 0.06],
  ["lead", "F#6", 32.261, 0.097],
  ["lead", "F#6", 32.509, 0.097],
  ["lead", "E6", 33.008, 0.1],
  ["lead", "E6", 33.259, 0.096],
  ["lead", "D#6", 33.754, 0.101],
  ["lead", "D#6", 34.006, 0.1],
  ["lead", "C#6", 34.505, 0.097],
  ["lead", "C#6", 34.753, 0.099],
  ["lead", "F#6", 36.261, 0.097],
  ["lead", "F#6", 36.509, 0.097],
  ["lead", "E6", 37.008, 0.1],
  ["lead", "E6", 37.259, 0.096],
  ["lead", "D#6", 37.754, 0.101],
  ["lead", "D#6", 38.006, 0.1],
  ["lead", "C#6", 38.505, 0.097],
  ["lead", "C#6", 38.753, 0.099],
  ["lead", "F#6", 40.261, 0.097],
  ["lead", "F#6", 40.509, 0.097],
  ["lead", "E6", 41.008, 0.1],
  ["lead", "E6", 41.259, 0.096],
  ["lead", "D6", 41.754, 0.101],
  ["lead", "D6", 42.006, 0.1],
  ["lead", "C#6", 42.505, 0.097],
  ["lead", "C#6", 42.753, 0.099],
  ["lead", "E6", 44.261, 0.097],
  ["lead", "E6", 44.509, 0.097],
  ["lead", "D6", 45.008, 0.1],
  ["lead", "D6", 45.259, 0.096],
  ["lead", "C#6", 45.754, 0.101],
  ["lead", "C#6", 46.006, 0.1],
  ["lead", "B5", 46.505, 0.097],
  ["lead", "B5", 46.753, 0.099],
  ["lead", "G#5", 48.509, 0.097],
  ["lead", "G#5", 48.757, 0.1],
  ["lead", "B5", 49.259, 0.096],
  ["lead", "F#5", 49.754, 1.746],
  ["lead", "F#5", 52.261, 0.097],
  ["lead", "F5", 52.757, 0.25],
  ["lead", "F#5", 53.008, 0.1],
  ["lead", "G#5", 53.259, 0.096],
  ["lead", "A5", 53.506, 0.097],
  ["lead", "B5", 53.754, 0.101],
  ["lead", "C#6", 54.006, 0.996],
  ["lead", "C6", 55.003, 0.099],
  ["lead", "B5", 56.001, 0.108],
  ["lead", "B5", 56.261, 0.097],
  ["lead", "B5", 56.509, 0.097],
  ["lead", "B5", 56.757, 0.1],
  ["lead", "B5", 57.008, 0.1],
  ["lead", "B5", 58.505, 0.097],
  ["lead", "B5", 58.753, 0.099],
  ["lead", "B5", 59.003, 0.099],
  ["lead", "B5", 59.253, 0.097],
  ["lead", "B5", 59.501, 0.098],
  ["lead", "B5", 60.001, 0.108],
  ["lead", "B5", 60.261, 0.097],
  ["lead", "B5", 60.509, 0.097],
  ["lead", "B5", 60.757, 0.1],
  ["lead", "B5", 61.008, 0.1],
  ["lead", "B5", 63.003, 0.125],
  ["lead", "B5", 63.129, 0.123],
  ["lead", "B5", 63.253, 0.123],
  ["lead", "B5", 63.377, 0.123],
  ["lead", "B5", 63.501, 0.086],
];

// Trololo (Eduard Khil, "I Am So Happy to Finally Be Back Home") — the C-major
// main section transcribed from a MIDI: the "ohhh" opening, the fast runs, and
// the descending triplet "tro-lo-lo" cascade, over a walking bass. 72-beat loop
// at 94 BPM. Elad's button plays this instead of Wii Shop (use-wii-shop-music.ts).
const TROLOLO_SONG: ReadonlyArray<readonly [Voice, string, number, number]> = [
  ["lead", "C4", 0, 3],
  ["bass", "C2", 0.01, 0.5],
  ["bass", "G1", 1.01, 0.5],
  ["bass", "C2", 2.01, 0.5],
  ["lead", "G3", 3, 0.75],
  ["bass", "G1", 3.01, 0.5],
  ["lead", "F3", 3.75, 0.25],
  ["lead", "G3", 4, 3],
  ["bass", "C2", 4.01, 0.5],
  ["bass", "G1", 5.01, 0.5],
  ["bass", "C2", 6.01, 0.5],
  ["lead", "C3", 7, 0.5],
  ["bass", "G1", 7.01, 0.5],
  ["lead", "D3", 7.5, 0.5],
  ["lead", "E3", 8, 2],
  ["bass", "C2", 8.01, 0.5],
  ["bass", "G1", 9.01, 0.5],
  ["lead", "G3", 10, 2],
  ["bass", "C2", 10.01, 0.5],
  ["bass", "G1", 11.01, 0.5],
  ["lead", "E3", 12, 0.75],
  ["bass", "D2", 12.01, 0.5],
  ["lead", "D3", 12.75, 1],
  ["bass", "G2", 13.01, 0.5],
  ["bass", "D2", 14.01, 0.5],
  ["lead", "G3", 15, 0.25],
  ["bass", "G1", 15.01, 0.5],
  ["lead", "G#3", 15.25, 0.25],
  ["lead", "A3", 15.5, 0.25],
  ["lead", "B3", 15.75, 0.25],
  ["lead", "C4", 16, 3],
  ["bass", "C2", 16.01, 0.5],
  ["bass", "D2", 17.01, 0.5],
  ["bass", "E2", 18.01, 0.5],
  ["lead", "G3", 19, 0.5],
  ["bass", "D2", 19.01, 0.5],
  ["lead", "F3", 19.5, 0.5],
  ["lead", "G3", 20, 3],
  ["bass", "C2", 20.01, 0.5],
  ["bass", "D2", 21.01, 0.5],
  ["bass", "E2", 22.01, 0.5],
  ["lead", "C3", 23, 0.5],
  ["bass", "D2", 23.01, 0.5],
  ["lead", "D3", 23.5, 0.5],
  ["lead", "E3", 24, 2],
  ["bass", "E2", 24.01, 0.5],
  ["bass", "A1", 25.01, 0.5],
  ["lead", "E3", 26, 2],
  ["bass", "D2", 26.01, 0.5],
  ["bass", "G2", 27.01, 0.5],
  ["lead", "D3", 28, 0.75],
  ["bass", "C2", 28.01, 0.5],
  ["lead", "C3", 28.75, 0.75],
  ["bass", "G1", 29.01, 0.5],
  ["bass", "C2", 30.01, 0.5],
  ["lead", "C3", 31, 0.25],
  ["lead", "E3", 31.25, 0.25],
  ["lead", "G3", 31.5, 0.25],
  ["lead", "C4", 31.75, 0.25],
  ["lead", "B3", 32, 0.5],
  ["bass", "D2", 32.01, 0.5],
  ["lead", "B3", 32.5, 0.25],
  ["lead", "G3", 32.75, 0.25],
  ["lead", "A3", 33, 0.5],
  ["bass", "G2", 33.01, 0.5],
  ["lead", "A3", 33.5, 0.25],
  ["lead", "F3", 33.75, 0.25],
  ["lead", "G3", 34, 0.5],
  ["bass", "D2", 34.01, 0.5],
  ["lead", "F2", 34.76, 0.25],
  ["lead", "G2", 35, 0.25],
  ["bass", "G2", 35.01, 0.5],
  ["lead", "B2", 35.25, 0.25],
  ["lead", "D3", 35.5, 0.25],
  ["lead", "F3", 35.75, 0.25],
  ["lead", "E3", 36, 0.5],
  ["bass", "C2", 36.01, 0.5],
  ["bass", "G2", 37.01, 0.5],
  ["bass", "C3", 38.01, 0.5],
  ["lead", "C3", 39, 0.25],
  ["bass", "G2", 39.01, 0.5],
  ["lead", "E3", 39.25, 0.25],
  ["lead", "G3", 39.5, 0.25],
  ["lead", "C4", 39.75, 0.25],
  ["lead", "B3", 40, 0.5],
  ["bass", "D2", 40.01, 0.5],
  ["lead", "B3", 40.5, 0.25],
  ["lead", "G3", 40.75, 0.25],
  ["lead", "A3", 41, 0.5],
  ["bass", "G2", 41.01, 0.5],
  ["lead", "A3", 41.5, 0.25],
  ["lead", "F3", 41.75, 0.25],
  ["lead", "G3", 42, 0.5],
  ["bass", "D2", 42.01, 0.5],
  ["lead", "G3", 43, 0.25],
  ["bass", "G2", 43.01, 0.5],
  ["lead", "G#3", 43.25, 0.25],
  ["lead", "A3", 43.5, 0.25],
  ["lead", "B3", 43.75, 0.25],
  ["lead", "C4", 44, 3],
  ["bass", "C2", 44.01, 0.5],
  ["bass", "D2", 45.01, 0.5],
  ["bass", "E2", 46.01, 0.5],
  ["lead", "G3", 47, 0.5],
  ["bass", "D2", 47.01, 0.5],
  ["lead", "F3", 47.5, 0.5],
  ["lead", "G3", 48, 2.5],
  ["bass", "C2", 48.01, 0.5],
  ["bass", "D2", 49.01, 0.5],
  ["bass", "E2", 50.01, 0.5],
  ["lead", "C3", 51, 0.5],
  ["bass", "D2", 51.01, 0.5],
  ["lead", "D3", 51.5, 0.5],
  ["lead", "E3", 52, 2],
  ["bass", "E2", 52.01, 0.5],
  ["bass", "A1", 53.01, 0.5],
  ["lead", "E3", 54, 2],
  ["bass", "D2", 54.01, 0.5],
  ["bass", "G2", 55.01, 0.5],
  ["lead", "D3", 56, 0.75],
  ["bass", "C2", 56.01, 0.5],
  ["lead", "C3", 56.75, 0.75],
  ["bass", "G2", 57.01, 0.5],
  ["bass", "C2", 58.01, 0.5],
  ["lead", "B2", 58.76, 0.25],
  ["lead", "C3", 59, 0.25],
  ["bass", "G2", 59.01, 0.5],
  ["lead", "E3", 59.25, 0.25],
  ["lead", "G3", 59.5, 0.25],
  ["lead", "C4", 59.75, 0.25],
  ["lead", "B3", 60, 0.333],
  ["bass", "D2", 60.01, 0.5],
  ["lead", "D4", 60.333, 0.333],
  ["lead", "B3", 60.667, 0.333],
  ["lead", "A3", 61, 0.333],
  ["bass", "G2", 61.01, 0.5],
  ["lead", "C4", 61.333, 0.333],
  ["lead", "A3", 61.667, 0.333],
  ["lead", "G3", 62, 0.333],
  ["bass", "D2", 62.01, 0.5],
  ["lead", "B3", 62.333, 0.333],
  ["lead", "G3", 62.667, 0.333],
  ["lead", "F3", 63, 0.333],
  ["bass", "G2", 63.01, 0.5],
  ["lead", "A3", 63.333, 0.333],
  ["lead", "F3", 63.667, 0.333],
  ["lead", "E3", 64, 0.75],
  ["bass", "C2", 64.01, 0.5],
  ["bass", "G2", 65.01, 0.5],
  ["bass", "C3", 66.01, 0.5],
  ["lead", "B2", 66.76, 0.25],
  ["lead", "C3", 67, 0.25],
  ["bass", "G2", 67.01, 0.5],
  ["lead", "E3", 67.25, 0.25],
  ["lead", "G3", 67.5, 0.25],
  ["lead", "C4", 67.75, 0.25],
  ["lead", "B3", 68, 0.5],
  ["bass", "D2", 68.01, 0.5],
  ["lead", "B3", 68.5, 0.25],
  ["lead", "G3", 68.75, 0.25],
  ["lead", "A3", 69, 0.5],
  ["bass", "G2", 69.01, 0.5],
  ["lead", "A3", 69.5, 0.25],
  ["lead", "F3", 69.75, 0.25],
  ["lead", "G3", 70, 1],
  ["bass", "D2", 70.01, 0.5],
  ["lead", "G3", 71, 0.25],
  ["bass", "G2", 71.01, 0.5],
  ["lead", "G#3", 71.25, 0.25],
  ["lead", "A3", 71.5, 0.25],
  ["lead", "B3", 71.75, 0.25],
];

interface Track {
  tempoBpm: number;
  loopBeats: number;
  song: ReadonlyArray<readonly [Voice, string, number, number]>;
}

const WII_SHOP: Track = { tempoBpm: 118, loopBeats: 64, song: WII_SHOP_SONG };
const TROLOLO: Track = { tempoBpm: 94, loopBeats: 72, song: TROLOLO_SONG };

function buildEvents(song: Track["song"]): NoteEvent[] {
  return song
    .map(([voice, note, beat, beats]) => ({ voice, freq: noteToFreq(note), beat, beats }))
    .sort((left, right) => left.beat - right.beat);
}

export interface WiiShopPlayer {
  start(): Promise<void>;
  stop(): void;
}

function createPlayer(track: Track): WiiShopPlayer {
  const secondsPerBeat = 60 / track.tempoBpm;
  const loopDurationS = track.loopBeats * secondsPerBeat;
  const events = buildEvents(track.song);

  let ctx: AudioContext | null = null;
  let master: GainNode | null = null;
  let timer: number | null = null;
  let loopStart = 0;
  let index = 0;
  let gestureHandler: (() => void) | null = null;
  let cancelled = false;
  const active = new Set<OscillatorNode>();

  function clearGesture(): void {
    if (gestureHandler) {
      window.removeEventListener("pointerdown", gestureHandler);
      window.removeEventListener("keydown", gestureHandler);
      gestureHandler = null;
    }
  }

  function playNote(event: NoteEvent, when: number): void {
    if (!ctx || !master) return;
    const params = VOICE[event.voice];
    const duration = Math.max(0.04, event.beats * secondsPerBeat * 0.95);
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = params.type;
    osc.frequency.value = event.freq;

    const attack = 0.005;
    const peak = params.gain;
    const sustainTime = when + Math.max(attack + 0.02, duration);
    gain.gain.setValueAtTime(0.0001, when);
    gain.gain.exponentialRampToValueAtTime(peak, when + attack);
    gain.gain.exponentialRampToValueAtTime(peak * params.sustain, sustainTime);
    gain.gain.exponentialRampToValueAtTime(0.0001, when + duration + params.release);

    osc.connect(gain);
    gain.connect(master);
    osc.start(when);
    osc.stop(when + duration + params.release + 0.02);
    active.add(osc);
    osc.onended = () => {
      active.delete(osc);
      osc.disconnect();
      gain.disconnect();
    };
  }

  // Incremental lookahead scheduler: only commits the next ~100 ms of notes per
  // tick, so we never burst-create hundreds of nodes at once.
  function scheduleTick(): void {
    if (!ctx || events.length === 0) return;
    const horizon = ctx.currentTime + SCHEDULE_AHEAD_S;
    while (true) {
      if (index >= events.length) {
        loopStart += loopDurationS;
        index = 0;
      }
      const event = events[index];
      if (!event) break;
      const when = loopStart + event.beat * secondsPerBeat;
      if (when >= horizon) break;
      playNote(event, when);
      index += 1;
    }
  }

  async function start(): Promise<void> {
    if (cancelled) return;
    if (!ctx) {
      ctx = new AudioContext();
      master = ctx.createGain();
      master.gain.value = MASTER_GAIN;
      master.connect(ctx.destination);
    }
    if (ctx.state === "suspended") {
      try {
        await ctx.resume();
      } catch {
        // resume() rejects when there has been no user gesture yet.
      }
    }
    // stop() may have run while we were awaiting resume(); don't resurrect.
    if (cancelled) return;
    // Browsers block audio until the user interacts with the page. If we are
    // still suspended, arm a one-shot gesture listener and try again then.
    if (ctx.state !== "running") {
      clearGesture();
      gestureHandler = () => {
        clearGesture();
        void start();
      };
      window.addEventListener("pointerdown", gestureHandler, { once: true });
      window.addEventListener("keydown", gestureHandler, { once: true });
      return;
    }
    if (timer !== null) return; // already playing
    if (master) {
      master.gain.cancelScheduledValues(ctx.currentTime);
      master.gain.setValueAtTime(MASTER_GAIN, ctx.currentTime);
    }
    loopStart = ctx.currentTime + 0.1;
    index = 0;
    scheduleTick();
    timer = window.setInterval(scheduleTick, LOOKAHEAD_MS);
  }

  function stop(): void {
    cancelled = true;
    clearGesture();
    if (timer !== null) {
      clearInterval(timer);
      timer = null;
    }
    if (!ctx || !master) return;
    const now = ctx.currentTime;
    master.gain.cancelScheduledValues(now);
    master.gain.setValueAtTime(master.gain.value, now);
    master.gain.linearRampToValueAtTime(0.0001, now + 0.08);
    for (const osc of active) {
      try {
        osc.stop(now + 0.1);
      } catch {
        // Already stopped — fine.
      }
    }
    active.clear();
    // Single-use player: release the AudioContext once the fade-out finishes so
    // repeated toggles don't leak contexts (browsers cap how many can exist).
    const closing = ctx;
    window.setTimeout(() => void closing.close(), 200);
  }

  return { start, stop };
}

export function createWiiShopPlayer(): WiiShopPlayer {
  return createPlayer(WII_SHOP);
}

export function createTrololoPlayer(): WiiShopPlayer {
  return createPlayer(TROLOLO);
}
